<template>
    <v-flex>
        <v-layout
            v-if="!monthHasAllowedAvailabilities"
            row
            wrap
            xs12
            class="ql-editor">
            <v-flex xs12>
                <tz-alert
                    v-if="displayNoAvailGeneralMessage"
                    :value="true"
                    color="white"
                    :outline="true"
                    :custom-outline="true"
                    :message="$t('appt.errors.noAvailability')"
                    outline-suffix="-warning"
                    bgclass="outline-warning"
                    override-alert-role
                />
                <tz-alert
                    v-if="displayNoAvailServiceMessage"
                    :value="true"
                    color="white"
                    :outline="true"
                    :custom-outline="true"
                    outline-suffix="-warning"
                    :message="selectedService['noAvailabilityMessage' + upperLocale]"
                    bgclass="outline-warning"
                    override-alert-role
                />
                <tz-alert
                    v-if="displayNoAvailSpecificMessage"
                    :value="true"
                    color="white"
                    :outline="true"
                    :custom-outline="true"
                    outline-suffix="-warning"
                    :message="getSpecificMessage(false)['message' + upperLocale]"
                    bgclass="outline-warning"
                    override-alert-role
                />
            </v-flex>
        </v-layout>
        <v-layout
            v-if="monthHasAllowedAvailabilities"
            row
            wrap
            xs12
            class="ql-editor">
            <v-flex xs12>
                <tz-alert
                    v-if="getSpecificMessage(true)"
                    :value="true"
                    color="white"
                    :custom-outline="true"
                    outline-suffix="-primary"
                    :message="getSpecificMessage(true)['message' + upperLocale]"
                    bgclass="outline-warning"
                    override-alert-role
                />
                <tz-alert
                    v-if="selectedService['availabilityMessage' + upperLocale]"
                    :value="true"
                    color="white"
                    :custom-outline="true"
                    outline-suffix="-primary"
                    :message="selectedService['availabilityMessage' + upperLocale]"
                    bgclass="outline-warning"
                    override-alert-role
                />
            </v-flex>
        </v-layout>
        <v-layout
            v-if="showToleranceMessage && showCalendar"
            row
            wrap
            xs12
            class="ql-editor">
            <v-flex xs12 class="text-center">
                <tz-alert
                    :value="true"
                    color="#fff"
                    bgclass="kidsTolerances"
                    :message="toleranceMessage"
                    override-alert-role
                />
            </v-flex>
        </v-layout>
    </v-flex>
</template>

<script lang="js">
import { find, includes, isEqual, isNil, map, reject, size } from 'lodash';
import { ServiceSettings, SubModulesEnum } from '@/enums';
import { stringDate } from '@/utils';
import TzAlert from '@/components/tzAlert/TzAlert';
import { mapGetters, mapMutations } from 'vuex';
import { analyticsEventHandler } from '@/mixins/ga-event-handler';

export default {
    name: 'step-calendar-header-messages',
    components: {
        TzAlert,
    },
    props: {
        selectedService: { required: false, type: Object },
        currentEstSettings: { required: false, type: Array },
        serviceFiltersAnswers: { required: false, type: Array },
        monthHasAllowedAvailabilities: { required: false, type: Number, default: 0 },
        delayTolerance: { required: false, type: Object },
    },
    data: () => ({
        // Loading
        loading: false,
    }),
    mixins: [analyticsEventHandler],
    created() {
        this.questionFiltersClientIsAdmissible();
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
            upperLocale: 'upperLocale',
            showCalendar: 'calendarDisplaySettings/showCalendar',
        }),
        serviceHasVaccineSubmodule: {
            get: function() { return includes(find(this.currentEstSettings, ['setting', ServiceSettings.SUBMODULES]).value, SubModulesEnum.VACCINE); },
        },
        serviceHasPregnancyDelaySubmodule: {
            get: function() { return includes(find(this.currentEstSettings, ['setting', ServiceSettings.SUBMODULES]).value, SubModulesEnum.PREGNANCY_DELAY); },
        },
        displayNoAvailGeneralMessage() {
            return !this.selectedService['noAvailabilityMessage' + this.upperLocale] && !this.getSpecificMessage(false);
        },
        displayNoAvailServiceMessage() {
            return this.selectedService['noAvailabilityMessage' + this.upperLocale] && !this.getSpecificMessage(false);
        },
        displayNoAvailSpecificMessage() {
            return !!this.getSpecificMessage(false);
        },
        target() {
            if (this.serviceHasVaccineSubmodule) {
                return this.$i18n.t('appt.targetVaccine');
            }
            if (this.serviceHasPregnancyDelaySubmodule) {
                return this.$i18n.t('appt.targetPregnancy');
            }
            return '';
        },
        showToleranceMessage() {
            return this.serviceHasVaccineSubmodule || this.serviceHasPregnancyDelaySubmodule || this.delayTolerance.maxDelayInDays < 9000;
        },
        toleranceMessage() {
            return this.target +
                this.formatApptDate(this.delayTolerance.minDelay) +
                this.$i18n.t('appt.to') +
                this.formatApptDate(this.delayTolerance.maxDelay);
        },
    },
    methods: {
        ...mapMutations({
            setClientFiltersIsAdmissible: 'services/SET_CLIENT_FILTERS_IS_ADMISSIBLE',
        }),
        /**
         * @description Returns the specific message of the current service for the current answers
         * @param {boolean} withAvails
         * @returns {undefined|string}
         */
        getSpecificMessage(withAvails) {
            const setting = find(this.currentEstSettings, ['setting', ServiceSettings.FILTER_MESSAGES]);
            if(setting) {
                const searchedType = withAvails ? 'with_avail' : 'without_avail';
                const filterMessages = setting.value;
                const answers = map(reject(this.serviceFiltersAnswers, isNil), (answer) => { return !(answer - 1) ? 1 : 0; });
                return find(filterMessages, (fm) => {
                    if (fm.type === searchedType) {
                        return isEqual(answers, fm.answers);
                    }
                    return false;
                });
            }
        },
        /**
         * @description Formats the appointment time
         * @param {Date} date
         * @returns {String}
         */
        formatApptDate(date) {
            return stringDate(date, this.locale);
        },

        questionFiltersClientIsAdmissible() {
            if (size(this.serviceFiltersAnswers) > 0) {
                if (this.monthHasAllowedAvailabilities) {
                    this.setClientFiltersIsAdmissible(true);
                }
                if (this.displayNoAvailSpecificMessage) {
                    this.setClientFiltersIsAdmissible(false);
                }
                this.publicQuestionFilterAnalytics();
            }
        }
    },
};
</script>
